import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface ExtractedData {
  vendor_name?: string;
  invoice_number?: string;
  invoice_date?: string;
  total_amount?: number;
  account_number?: string;
  line_items?: any[];
  confidence?: number;
}

const VendorInvoiceUploadPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [editableData, setEditableData] = useState<ExtractedData>({});
  const [saving, setSaving] = useState<boolean>(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
        setExtractedData(null);
        setInvoiceId(null);
      } else {
        showErrorSnackBar('Please select a PDF file');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showErrorSnackBar('Please select a PDF file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('pdf_file', selectedFile);

    try {
      const response: any = await axios.post('/vendors/vendor_invoices/upload_and_extract', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const extracted = response.data.extracted_data;
      setExtractedData(extracted);
      setEditableData(extracted);
      setInvoiceId(response.data.invoice.id);
      showSuccessSnackBar('PDF uploaded and processed successfully!');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to upload and process PDF';
      showErrorSnackBar(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!invoiceId) return;

    setSaving(true);
    try {
      await axios.put(`/vendors/vendor_invoices/${invoiceId}`, {
        vendor_invoice: {
          vendor_name: editableData.vendor_name,
          invoice_number: editableData.invoice_number,
          invoice_date: editableData.invoice_date,
          total_amount: editableData.total_amount,
          account_number: editableData.account_number,
        },
      });
      showSuccessSnackBar('Invoice updated successfully!');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update invoice';
      showErrorSnackBar(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleViewInvoice = () => {
    if (invoiceId) {
      history.push(`/vendors/invoices/${invoiceId}`);
    }
  };

  return (
    <Box style={{ width: '100%' }}>
      <Typography variant="h5" component="h5" style={{ marginBottom: 20 }}>
        Upload Vendor Invoice
      </Typography>

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Upload PDF Invoice
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Upload a vendor invoice PDF to automatically extract data using Amazon Textract
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <input
                accept="application/pdf"
                style={{ display: 'none' }}
                id="pdf-upload-button"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="pdf-upload-button">
                <Button variant="outlined" component="span" fullWidth startIcon={<CloudUploadIcon />}>
                  Select PDF File
                </Button>
              </label>
            </Grid>

            {selectedFile && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Selected file: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(2)} KB)
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                onClick={handleUpload}
                variant="contained"
                color="primary"
                disabled={!selectedFile || uploading}
                startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                fullWidth
              >
                {uploading ? 'Processing...' : 'Upload and Extract Data'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {extractedData && (
        <>
          <Box mt={3}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Extracted Data
                    </Typography>
                    {extractedData.confidence && (
                      <Alert
                        severity={extractedData.confidence > 75 ? 'success' : 'warning'}
                        style={{ marginBottom: 16 }}
                      >
                        Extraction Confidence: {extractedData.confidence}%
                      </Alert>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info">
                      You can edit any fields below that weren't extracted correctly. Click "Save Changes" to update the
                      invoice.
                    </Alert>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Vendor Name"
                      value={editableData.vendor_name || ''}
                      onChange={(e) => setEditableData({ ...editableData, vendor_name: e.target.value })}
                      fullWidth
                      variant="outlined"
                      placeholder="Enter vendor name"
                      helperText={!extractedData.vendor_name ? 'Not extracted - please enter manually' : ''}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Invoice Number"
                      value={editableData.invoice_number || ''}
                      onChange={(e) => setEditableData({ ...editableData, invoice_number: e.target.value })}
                      fullWidth
                      variant="outlined"
                      placeholder="Enter invoice number"
                      helperText={!extractedData.invoice_number ? 'Not extracted - please enter manually' : ''}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Invoice Date"
                      type="date"
                      value={editableData.invoice_date || ''}
                      onChange={(e) => setEditableData({ ...editableData, invoice_date: e.target.value })}
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      helperText={!extractedData.invoice_date ? 'Not extracted - please enter manually' : ''}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Total Amount"
                      value={editableData.total_amount || ''}
                      onChange={(e) =>
                        setEditableData({ ...editableData, total_amount: parseFloat(e.target.value) || 0 })
                      }
                      fullWidth
                      variant="outlined"
                      type="number"
                      placeholder="Enter total amount"
                      helperText={!extractedData.total_amount ? 'Not extracted - please enter manually' : ''}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Account Number"
                      value={editableData.account_number || ''}
                      onChange={(e) => setEditableData({ ...editableData, account_number: e.target.value })}
                      fullWidth
                      variant="outlined"
                      placeholder="Enter account number"
                      helperText={!extractedData.account_number ? 'Not extracted - please enter manually' : ''}
                    />
                  </Grid>

                  {extractedData.line_items && extractedData.line_items.length > 0 && (
                    <Grid item xs={12}>
                      <Divider style={{ margin: '16px 0' }} />
                      <Typography variant="subtitle1" gutterBottom>
                        Line Items ({extractedData.line_items.length})
                      </Typography>
                      {extractedData.line_items.map((item: any, index: number) => (
                        <Box key={index} mt={2} p={2} border={1} borderColor="grey.300" borderRadius={2}>
                          <Typography variant="body2">
                            <strong>Description:</strong> {item.description || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Quantity:</strong> {item.quantity || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Unit Price:</strong> {item.unit_price ? `$${item.unit_price}` : 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Total:</strong> {item.total_price ? `$${item.total_price}` : 'N/A'}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <Button
                      onClick={handleSaveChanges}
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : null}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button onClick={handleViewInvoice} variant="outlined" color="primary" fullWidth>
                      View Invoice Details
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
};

export default VendorInvoiceUploadPage;
